import { getLogger } from '@logtape/logtape';
import { Card } from '@mantine/core';
import { FC, useState } from 'react';

import TaskForm from '@/components/TaskModal/TaskForm.tsx';
import TaskDetails from '@/components/TaskView/TaskDetails/TaskDetails.tsx';

import usePvsPractitioners from '@/hooks/usePvsPractitioners.ts';
import { usePvsUserRoles } from '@/hooks/usePvsUserRoles.ts';
import useTask, { mutateTask } from '@/hooks/useTask.ts';
import useTasks from '@/hooks/useTasks.ts';

import { ModalType } from '@/models/Modal.ts';
import { TaskModalMode } from '@/models/TaskModalMode.ts';
import { TaskPrefill } from '@/models/TaskPrefill.ts';
import { DCTaskStatus, EnrichedTask } from '@/models/doc-cirrus/DCTask.ts';

import { closeModal } from '@/services/modalService.tsx';
import { showErrorNotification } from '@/services/notificationService.ts';
import { cancelTask, updateTaskAndEnrich } from '@/services/tasksService.ts';

import { useTaskViewStore } from '@/stores/taskViewStore.ts';

const logger = getLogger(['component', 'task-modal']);

interface Props {
    prefill?: TaskPrefill;
    onSaved?: (task: EnrichedTask) => void;
    onDeleted?: (task: EnrichedTask) => void;
}

const TaskModal: FC<Props> = ({ prefill, onSaved, onDeleted }) => {
    const { pvs_user_roles } = usePvsUserRoles();
    const mode = useTaskViewStore((state) => state.task_modal_mode);
    const task_id = useTaskViewStore((state) => state.selected_task_id);
    const task_instance_id = useTaskViewStore((state) => state.selected_task_instance_id);

    // Fetch task data for editing mode
    const { task, is_loading: is_loading_task } = useTask({ task_id: task_id, instance_id: task_instance_id });
    const { task_types, updateOrCreateTask, removeTaskOptimistically } = useTasks();
    const { instance_users_map } = usePvsPractitioners();

    const [cta_assign_to_me_loading, setCtaAssignToMeLoading] = useState(false);
    const [cta_mark_as_done_loading, setCtaMarkAsDoneLoading] = useState(false);
    const [cta_delete_loading, setCtaDeleteLoading] = useState(false);

    const handleAssignToMe = async (): Promise<void> => {
        if (!task_instance_id || !task_id || !task) return;

        const employee_id = pvs_user_roles.find((role) => role.instance_id === task_instance_id)?.pvs_user_id;

        try {
            setCtaAssignToMeLoading(true);

            const updated_task_data = { ...task, status: DCTaskStatus.ASSIGNED, employee_id: employee_id };
            await updateOrCreateTask(async () => {
                const updated_task = await updateTaskAndEnrich(
                    task_instance_id,
                    task_id,
                    updated_task_data,
                    task_types,
                    instance_users_map
                );
                if (!updated_task) {
                    throw new Error('Failed to assign task to me');
                }

                useTaskViewStore.setState((state) => ({
                    task_list: state.task_list.map((t) => (task.id === t.id ? updated_task : t)),
                }));

                return updated_task;
            });

            await mutateTask(task_id, task_instance_id);
        } catch (error) {
            logger.error('Failed to assign the task', { error: error });
            // EN: Error while assigning task to me
            showErrorNotification('Fehler beim Zuweisen der Aufgabe a mich');
        }

        setCtaAssignToMeLoading(false);
    };

    const handleMarkAsDone = async (): Promise<void> => {
        if (!task_instance_id || !task_id || !task) return;

        try {
            setCtaMarkAsDoneLoading(true);

            const updated_task_data = { ...task, status: DCTaskStatus.DONE };
            const savedTask = await updateOrCreateTask(async () => {
                const updated_task = await updateTaskAndEnrich(
                    task_instance_id,
                    task_id,
                    updated_task_data,
                    task_types,
                    instance_users_map
                );
                if (!updated_task) {
                    throw new Error('Failed to mark task as done');
                }
                return updated_task;
            });

            await mutateTask(task_id, task_instance_id);

            if (onSaved) {
                onSaved(savedTask);
                closeModal(ModalType.TASK);
            }
        } catch (error) {
            logger.error('Failed to mark the task as done', { error: error });
            // EN: Error while marking task as done
            showErrorNotification('Fehler beim Markieren der Aufgabe als erledigt');
        }

        setCtaMarkAsDoneLoading(false);
    };

    const handleDelete = async (): Promise<void> => {
        if (!task_instance_id || !task_id) return;

        setCtaDeleteLoading(true);

        // Hoisted out of the try/catch: React Compiler bails on value blocks (?./||/ternary) inside try.
        const notifyDeleted: (task: EnrichedTask) => void = onDeleted ?? (() => undefined);

        try {
            await removeTaskOptimistically(task_id, async () => {
                await cancelTask(task_instance_id, task_id);
            });

            if (task) notifyDeleted(task);
            closeModal(ModalType.TASK);
        } catch (error) {
            logger.error('Failed to delete the task', { error: error });
            // EN: Error while deleting task
            showErrorNotification('Fehler beim Löschen der Aufgabe');
        }

        setCtaDeleteLoading(false);
    };

    return (
        <Card className="relative flex-grow gap-4 rounded-lg shadow-md xl:p-6">
            {mode === TaskModalMode.DETAILS && (
                <TaskDetails
                    isCtaAssignToMeLoading={cta_assign_to_me_loading}
                    isCtaMarkAsDoneLoading={cta_mark_as_done_loading}
                    isCtaDeleteLoading={cta_delete_loading}
                    onAssignToMe={handleAssignToMe}
                    onMarkAsDone={handleMarkAsDone}
                    onDelete={handleDelete}
                />
            )}
            {mode === TaskModalMode.EDITOR && (
                <TaskForm task={task} is_loading_task={is_loading_task} prefill={prefill} onSaved={onSaved} />
            )}
        </Card>
    );
};

export default TaskModal;
